/* eslint-disable @angular-eslint/directive-selector */
import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  HostListener,
  AfterContentInit,
  Output,
} from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { AutocompleteHandlerComponent } from './autocomplete-handler.component';
import { takeUntil, take, takeWhile } from 'rxjs/operators';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import {
  IAutocompleteOption,
  AutocompleteOptionHoverEvent,
  AutocompleteOptionClickEvent,
  AutocompleteValueSelectEvent,
} from './autocomplete.model';
import { EventEmitter } from '@angular/core';
import { PopularCountrySuggestionsComponent } from '../../../../../../qqc/features/destination/src/lib/popularCountrySuggestions/popularCountrySuggestions.component';

@Directive({
  selector: '[autocompleteHandler]',
})
export class AutocompleteDirective implements OnDestroy, AfterContentInit {
  @Input() autocompleteHandler!: AutocompleteHandlerComponent;
  @Input() onSearchKeyChanged!: Observable<string>;
  @Input() onFilteredOptionsPopulated!: Observable<IAutocompleteOption[]>;
  @Input() onSelectedOptionsChanged!: Observable<IAutocompleteOption[]>;
  @Output() valueSelected = new EventEmitter<AutocompleteValueSelectEvent>();
  @Output() optionsPanelClosed = new EventEmitter();
  @Input() popularCountrySuggestions!: PopularCountrySuggestionsComponent;
  panelStatus$ = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();
  private keyManager!: ActiveDescendantKeyManager<IAutocompleteOption>;

  private get hostNativeElement() {
    return this.host.nativeElement;
  }

  private get activeOption(): IAutocompleteOption | undefined {
    return this.keyManager?.activeItem ?? undefined;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!event || !event.key) {
      return;
    }
    this.keyManager.onKeydown(event);
  }

  @HostListener('keydown.enter', ['$event'])
  onKeyEnter(): void {
    this.notifyValueSelected();
    this.closeOptionsPanel();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelStatus$.value) {
      return;
    }
    const clickOnHost = this.doesClickOnTargetElement(
      event,
      this.hostNativeElement
    );
    const clickOnOverlay = this.doesClickOnTargetElement(
      event,
      this.autocompleteHandler.rootTemplate.nativeElement
    );
    const clickOnPopularCountrySuggestions = this.doesClickOnTargetElement(
      event,
      this.popularCountrySuggestions?.rootTemplate?.nativeElement
    );
    if (!clickOnHost && !clickOnOverlay && !clickOnPopularCountrySuggestions) {
      this.closeOptionsPanel();
    }
  }

  @HostListener('focus', ['$event'])
  @HostListener('click', ['$event'])
  openOptionalPanel(): void {
    this.createOptionsPanel();
    this.whenClickOption().subscribe(() => {
      this.notifyValueSelected();
      this.closeOptionsPanel();
    });
    this.whenHoverOption().subscribe((event: AutocompleteOptionHoverEvent) => {
      this.setActiveOption(event.option.index ?? 0);
    });
  }

  constructor(private host: ElementRef<HTMLInputElement>) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterContentInit(): void {
    this.keyManager = new ActiveDescendantKeyManager(
      this.autocompleteHandler.optionComponents
    ).withWrap();
    this.whenSearchKeyChanged().subscribe((searchKey: string) => {
      if (!searchKey) {
        return;
      }
      this.openOptionalPanel();
    });
    this.whenFilteredOptionsPopulated().subscribe(() => {
      setTimeout(() => {
        this.keyManager.setFirstItemActive();
      });
    });
    this.whenSelectedOptionsChanged().subscribe(
      (selectedOptions: IAutocompleteOption[]) => {
        if (!selectedOptions || selectedOptions.length === 0) {
          this.hostNativeElement.focus();
          this.keyManager.setActiveItem(-1);
        }
      }
    );
  }

  private notifyValueSelected(): void {
    if (this.activeOption) {
      this.valueSelected.emit({
        option: {
          value: this.activeOption.value,
          text: this.activeOption.text,
          id: this.activeOption.id,
        },
      });
    }
  }

  private setActiveOption(index: number): void {
    this.keyManager?.setActiveItem(index);
  }

  private createOptionsPanel(): void {
    this.autocompleteHandler.isHidden = false;
    this.panelStatus$.next(true);
    // return optionPanel;
  }

  private closeOptionsPanel(): void {
    this.autocompleteHandler.isHidden = true;
    this.panelStatus$.next(false);
    this.optionsPanelClosed.emit();
  }

  private whenSearchKeyChanged(): Observable<string> {
    return this.onSearchKeyChanged.pipe(takeUntil(this.destroy$));
  }

  private whenFilteredOptionsPopulated(): Observable<IAutocompleteOption[]> {
    return this.onFilteredOptionsPopulated.pipe(takeUntil(this.destroy$));
  }

  private whenSelectedOptionsChanged(): Observable<IAutocompleteOption[]> {
    return this.onSelectedOptionsChanged.pipe(takeUntil(this.destroy$));
  }

  private whenClickOption(): // optionsPanel: OverlayRef
  Observable<AutocompleteOptionClickEvent> {
    return this.autocompleteHandler.optionsClick().pipe(take(1));
  }

  private whenHoverOption(): Observable<AutocompleteOptionHoverEvent> {
    return this.autocompleteHandler
      .optionsHover()
      .pipe(takeWhile(() => this.panelStatus$.value));
  }

  private doesClickOnTargetElement(
    clickEvent: MouseEvent,
    targetElement: HTMLElement
  ): boolean {
    let clickTarget = clickEvent.target as HTMLElement | null;
    let clickOnHost = true;
    while (clickTarget !== null) {
      clickOnHost = clickTarget === targetElement; // the input
      if (clickOnHost) {
        break;
      }
      clickTarget = clickTarget.parentElement;
    }
    return clickOnHost;
  }
}
